import { ClientError } from '@tribeplatform/gql-client/lib'
import {
  AdvanceReadMessageOffsetResult,
  Chat,
  ChatParticipant,
  MutationAdvanceReadMessageOffsetArgs,
} from '@tribeplatform/gql-client/types'

import { useQueryClient } from '../../index.js'
import { useMutation } from '../../lib/react-query/useMutation.js'
import { useTribeClient } from '../../useTribeClient.js'
import {
  getChatKey,
  getUnreadMessagesCountKey,
} from '../../utils/keys/messaging.key.js'
import { useChatsReadOffset } from './useChatsReadOffset.js'
import { useUnreadMessagesCount } from './useUnreadMessagesCount.js'

type MutationAdvanceReadMessageOffsetArgsWithPos =
  MutationAdvanceReadMessageOffsetArgs & {
    pos: number
    archived?: boolean
  }

export const useAdvanceReadMessageOffset = () => {
  const { client } = useTribeClient()
  const queryClient = useQueryClient({})
  const { setChatReadOffset } = useChatsReadOffset()
  const { setUnreadMessagesCount } = useUnreadMessagesCount()

  return useMutation<
    AdvanceReadMessageOffsetResult,
    ClientError,
    MutationAdvanceReadMessageOffsetArgsWithPos
  >(
    (args: MutationAdvanceReadMessageOffsetArgsWithPos) =>
      client.messaging.advanceReadMessageOffset({
        ...args,
        chatId: args.chatId,
        input: {
          ...args.input,
        },
        returnNewCount: true,
      }),
    {
      onSuccess: (data, variables) => {
        const { input } = variables

        /**
         * Optimistically update new unread counts for the chat
         */

        const unreadMessagesCountKey = getUnreadMessagesCountKey({
          variables: data.chatId,
        })
        queryClient.setQueryData<AdvanceReadMessageOffsetResult>(
          unreadMessagesCountKey,
          data,
        )
        setUnreadMessagesCount(data.chatId, data.count, variables.archived)
        setChatReadOffset(data.chatId, variables.pos)

        if (!data.changed) {
          return data
        }

        /**
         * Optimistically update the current user offset for the chat
         */
        const chatKey = getChatKey({
          variables: {
            id: variables.chatId,
          },
        })
        const chat = queryClient.getQueryData<Chat>(chatKey)
        if (chat) {
          const updatedParticipant = {
            ...(chat.currentParticipant ?? {}),
            lastReadMessage: {
              pageId: input.pageId,
              messageId: input.messageId,
              signature: input.signature,
            },
          } as ChatParticipant

          const updatedChat: Chat = {
            ...chat,
            currentParticipant: updatedParticipant,
          }

          queryClient.setQueryData<Chat>(chatKey, updatedChat)
        }

        return data
      },
    },
  )
}
